import { Grid, Stack, Text } from '@chakra-ui/react';
import { DocumentType, OwnedDocument } from '@pelicargo/types';
import { modelUtils } from '@pelicargo/utils';
import { useMemo } from 'react';

import { trpc } from '../../config/trpc';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { useTenant } from '../../hooks/useTenant';
import { DocumentRow } from './DocumentRow';

type Props = {
  documents: OwnedDocument[];
  requestId?: number;
  expectedTypes?: DocumentType[];
  labelByDocumentId?: Record<number, string>;
  titleVariant?: 'subtle' | 'strong';
  columns?: number;
};

const TITLE_STYLES = {
  subtle: {
    fontSize: 'xs',
    fontWeight: 'semibold',
    textTransform: 'uppercase',
    color: 'text.muted',
    letterSpacing: 'wide',
  },
  strong: { fontSize: 'sm', fontWeight: 'bold', color: 'text.paragraph' },
} as const;

/**
 * Renders every document on a shipment, grouped by the account that uploaded it. See
 * modelUtils.documents.groupByAccount — nothing is filtered out, whoever is looking.
 */
export const DocumentGroups = ({
  documents,
  requestId,
  expectedTypes,
  labelByDocumentId,
  titleVariant = 'subtle',
  columns = 2,
}: Props) => {
  const { targetAccount } = useTargetAccount();
  const { info } = useTenant();

  // Shares the cache with the request query the rows already make. An FP on a
  // non-AFR quote isn't allowed this request, and then the expected documents fall
  // back to their own group rather than being pinned on the FP.
  const { data: request } = trpc.getRequest.useQuery(
    { id: requestId },
    { enabled: !!requestId, refetchOnWindowFocus: false, retry: false },
  );

  const groups = useMemo(
    () =>
      modelUtils.documents.groupByAccount({
        documents,
        viewerAccountId: targetAccount?.id,
        requestAccount: request?.account,
        tenantTitle: info?.name,
        expectedTypes,
        labelByDocumentId,
      }),
    [documents, expectedTypes, info?.name, labelByDocumentId, request?.account, targetAccount?.id],
  );

  return (
    <Stack spacing="3">
      {groups.map((group) => (
        <Stack key={group.key} spacing="1">
          <Text {...TITLE_STYLES[titleVariant]}>{group.title}</Text>
          <Grid
            templateColumns={{ base: '1fr', md: `repeat(${columns}, minmax(0, 1fr))` }}
            columnGap="6"
            rowGap="1"
            alignItems="start"
          >
            {group.documents.map((document, index) => (
              <DocumentRow
                key={document.id ?? `${group.key}-${document.type}-${index}`}
                document={document}
                label={document.label}
              />
            ))}
          </Grid>
        </Stack>
      ))}
    </Stack>
  );
};
